<template>
  <div>
    <base-header class="pb-6">
      <b-row align-v="center" class="py-4">
        <b-col cols="7" lg="6">
          <h6 class="h2 text-white d-inline-block mb-0">{{ year }} Budget Model</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </b-col>
        <b-col lg="6" cols="5" class="text-right">
          <base-button size="sm" type="neutral" v-b-toggle.filters>
            <span class="btn-inner--icon"><i class="fas fa-filter"></i></span>
              <span class="btn-inner--text">Filters</span>
          </base-button>
        </b-col>
      </b-row>
    </base-header>
    <b-container fluid class="mt--6">
      <observed-upgrade-overlay :title="year + ' Budget Model'" :upgradeImg="'overlays/budget-model.png'" :placeholderImg="'placeholders/budget-model.png'" v-if="!subscribed"></observed-upgrade-overlay>
      <div v-else>
        <loading-panel v-if="loading"></loading-panel>
        <div v-else>
          <div class="text-right mb-3">
            <userpilot-flow :id="'1672860540eMyl4564'" :variant="'neutral'"></userpilot-flow>
          </div>
          <b-collapse visible id="filters">
            <b-card>
              <base-input label="Year" label-for="yearInput">
                <el-select v-model="year">
                  <el-option v-for="year in years"
                             :key="year.label"
                             :label="year.label"
                             :value="year.value">
                  </el-option>
                </el-select>
              </base-input>
            </b-card>
          </b-collapse>
          <b-row>
            <b-col>
              <stats-card title=""
                          type="gradient-info"
                          :sub-title="yearlyExpenses | currency"
                          icon="fas fa-dollar-sign">

                <template slot="footer">
                  <span class="text-nowrap">{{ year }} Business Plan Allocated Funds from Economic Model</span>
                </template>
              </stats-card>
            </b-col>
            <b-col>
              <stats-card title=""
                          type="gradient-success"
                          :sub-title="expensePercent + '%'"
                          icon="fas fa-percent">

                <template slot="footer">
                  <span class="text-nowrap">{{ year }} Expense % Projections from Economic Model</span>
                </template>
              </stats-card>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="3">
              <b-card no-body>
                <b-card-header>
                  <h3 class="mb-0">Add New Budget Item</h3>
                  <userpilot-flow :id="'1670594824eXaj786'" :variant="'outline-primary'"></userpilot-flow>
                </b-card-header>
                <b-card-body>
                  <validation-observer v-slot="{handleSubmit}" ref="formValidator">
                    <form class="needs-validation" ref="transactionForm"
                          @submit.prevent="handleSubmit(addExpense)">
                      <base-input label="P&L Category" label-for="category" rules="required" name="P&L Category">
                        <el-select v-model="form.category">
                          <el-option v-for="option in options"
                                     :key="option.label"
                                     :label="option.label"
                                     :value="option.value">
                          </el-option>
                        </el-select>
                      </base-input>

                      <base-input label="Expense Description" label-for="description">
                        <html-editor v-model="form.description" placeholder="Type in the expense you wish to add to your Annual Budget. The more detailed, the better!"></html-editor>
                      </base-input>

                      <base-input label="Monthly Expense"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  rules="required"
                                  name="Monthly Expense"
                                  prependIcon="fas fa-dollar-sign"
                                  v-model="form.monthly_expense">
                      </base-input>

                      <base-input label="Annual Expense"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  rules="required"
                                  name="Annual Expense"
                                  prependIcon="fas fa-dollar-sign"
                                  v-model="form.annual_expense">
                      </base-input>

                      <base-button block type="success" native-type="submit">Add Expense</base-button>
                    </form>
                  </validation-observer>
                </b-card-body>
              </b-card>
            </b-col>
            <b-col md="9">
              <b-row>
                <b-col>
                  <b-card no-body>
                    <b-card-header>
                      <h3 class="mb-0">EXAMPLE: Breakdown of Expenses</h3>
                    </b-card-header>
                    <b-table small responsive :items="examples">
                        <template #cell(category)="data">
                          {{ data.item.category }}
                        </template>
                        <template #cell(description)="data">
                          <span v-html="data.item.description"></span>
                        </template>
                        <template #cell(monthly_expense)="data">
                          {{ data.item.monthly_expense | currency }}
                        </template>
                        <template #cell(annual_expense)="data">
                          {{ data.item.annual_expense | currency }}
                        </template>
                        <template #cell(actions)="data">
                        </template>
                    </b-table>
                  </b-card>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-card no-body>
                    <b-card-header>
                      <h3 class="mb-0">Breakdown of Expenses</h3>
                    </b-card-header>
                    <b-table small responsive :items="items" :fields="breakdownFields" :per-page="perPage" :current-page="currentPage" id="breakdown">
                        <template #cell(category)="data">
                          <base-input label-for="category" rules="required" name="P&L Category" placeholder="P&L Category" v-if="editable === data.index">
                            <el-select v-model="data.item.category">
                              <el-option v-for="option in options"
                                         :key="option.label"
                                         :label="option.label"
                                         :value="option.value">
                              </el-option>
                            </el-select>
                          </base-input>
                          <span v-else>{{ options[data.item.category].label }}</span>
                        </template>
                        <template #cell(description)="data">
                          <html-editor v-model="data.item.description" placeholder="Type in the expense you wish to add to your Annual Budget. The more detailed, the better!" v-if="editable === data.index"></html-editor>
                          <span v-html="data.item.description" class="bd-html" v-else></span>
                        </template>
                        <template #cell(monthly_expense)="data">
                          <b-input-group size="sm" prepend="$" v-if="editable === data.index" style="min-width: 100px">
                            <b-form-input size="lg" type="number" min="0.01" v-model="data.item.monthly_expense" />
                          </b-input-group>
                          <span v-else>{{ data.item.monthly_expense | currency }}</span>
                        </template>
                        <template #cell(annual_expense)="data">
                          <b-input-group size="sm" prepend="$" v-if="editable === data.index" style="min-width: 100px">
                            <b-form-input size="lg" type="number" min="0.01" v-model="data.item.annual_expense" />
                          </b-input-group>
                          <span v-else>{{ data.item.annual_expense | currency }}</span>
                        </template>
                        <template #cell(actions)="data">
                          <b-button-group v-if="editable === data.index" boundary="viewport">
                            <b-button size="sm" variant="success" @click="handleUpdate(data)"><i class="fa fa-check"></i> Save</b-button>
                            <b-button size="sm" variant="danger" @click="handleCancel"><i class="fa fa-times"></i> Cancel</b-button>
                          </b-button-group>
                          <b-button-group v-else boundary="viewport">
                            <b-button size="sm" variant="primary" @click="handleEdit(data)"><i class="fa fa-edit"></i> Edit</b-button>
                            <b-button size="sm" variant="danger" @click="handleDelete(data)"><i class="fa fa-trash"></i> Delete</b-button>
                          </b-button-group>
                        </template>
                    </b-table>
                    <div class="d-flex justify-content-center mt-3" id="pagination">
                      <b-pagination v-model="currentPage" :total-rows="rows" :per-page="perPage" aria-controls="sources"></b-pagination>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-card no-body>
                    <b-card-header>
                      <h3 class="mb-0">Your {{ year }} Budget Model</h3>
                    </b-card-header>
                    <b-table small responsive :items="budgetModel" :fields="fields" foot-clone>
                      <template #cell(category)="data">
                        {{ data.item.category }}
                      </template>
                      <template #cell(allocation)="data">
                        {{ ((data.item.annual_expense / companyGci) * 100).toFixed(2) }}% of total GCI
                      </template>
                      <template #cell(annual_expense)="data">
                       {{ data.item.annual_expense | currency }}
                      </template>
                      <template #cell(monthly_expense)="data">
                        <span>{{ data.item.monthly_expense | currency }}</span>
                      </template>
                      <template #foot(category)="data">
                        Totals:
                      </template>
                      <template #foot(allocation)="data">
                        <span>{{ breakdownAllocation.toFixed(2) }}%</span>
                      </template>
                      <template #foot(annual_expense)="data">
                        <span>{{ annualTotal | currency }}</span>
                      </template>
                      <template #foot(monthly_expense)="data">
                        <span>{{ monthlyTotal | currency}}</span>
                      </template>
                    </b-table>
                  </b-card>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-container>
  </div>
</template>
<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.css';
import RouteBreadCrumb from '@/components/Breadcrumb/RouteBreadcrumb';
import LoadingPanel from '@/components/LoadingPanel';
import {Select, Option} from 'element-ui';
import HtmlEditor from '@/components/Inputs/HtmlEditor';
import economicModelMixin from '@/mixins/economicModel-mixin';
import errorsMixin from '@/mixins/errors-mixin';
import ObservedUpgradeOverlay from '@/views/Components/Overlays/ObservedUpgradeOverlay';
import UserpilotFlow from '@/views/Components/UserpilotFlow';

export default {
  mixins: [economicModelMixin, errorsMixin],
  components: {
    RouteBreadCrumb,
    LoadingPanel,
    [Select.name]: Select,
    [Option.name]: Option,
    HtmlEditor,
    ObservedUpgradeOverlay,
    UserpilotFlow
  },
  data() {
    return {
      loading: true,
      year: new Date().getFullYear(),
      years: [
        { value: new Date().getFullYear() - 6, label: new Date().getFullYear() - 6 },
        { value: new Date().getFullYear() - 5, label: new Date().getFullYear() - 5 },
        { value: new Date().getFullYear() - 4, label: new Date().getFullYear() - 4 },
        { value: new Date().getFullYear() - 3, label: new Date().getFullYear() - 3 },
        { value: new Date().getFullYear() - 2, label: new Date().getFullYear() - 2 },
        { value: new Date().getFullYear() - 1, label: new Date().getFullYear() - 1 },
        { value: new Date().getFullYear(), label: new Date().getFullYear() },
        { value: new Date().getFullYear() + 1, label: new Date().getFullYear() + 1 },
        { value: new Date().getFullYear() + 2, label: new Date().getFullYear() + 2 },
        { value: new Date().getFullYear() + 3, label: new Date().getFullYear() + 3 },
      ],
      items: null,
      budgetModel: [
        {
          category: '',
          annual_expense: 0,
          monthly_expense: 0,
        }
      ],
      fields: ['category', 'allocation', 'annual_expense', 'monthly_expense'],
      reccoFields: [
        {
          key: 'category',
          label: 'Category'
        },
        {
          key: 'allocation',
          label: '% Allocation'
        },
        {
          key: 'amount',
          label: 'Suggested Gross Amount'
        },
        {
          key: 'expense',
          label: 'Monthly Expense'
        }
      ],
      reccoItems: [
        {category: 'Compensation', allocation: 12.0},
        {category: 'Lead Generation', allocation: 9.2},
        {category: 'Occupancy', allocation: 2.0},
        {category: 'Communication & Technology', allocation: 1.5},
        {category: 'Automobile', allocation: 1.0},
        {category: 'Supplies/Office Expenses', allocation: 1.0},
        {category: 'Education & Coaching', allocation: 1.0},
        {category: 'Equipment/Furnishings', allocation: 1.0},
        {category: 'Insurance', allocation: 0.5}
      ],
      form: {
        year: this.year,
        category: null,
        description: '',
        monthly_expense: null,
        annual_expense: null
      },
      options: [
        { value: null, label: 'Please select a category'},
        { value: 1, label: 'Compensation'},
        { value: 2, label: 'Lead Generation'},
        { value: 3, label: 'Occupancy'},
        { value: 4, label: 'Communication & Technology'},
        { value: 5, label: 'Automobile'},
        { value: 6, label: 'Supplies/Office Expenses'},
        { value: 7, label: 'Education & Coaching'},
        { value: 8, label: 'Equipment/Furnishings'},
        { value: 9, label: 'Insurance'}
      ],
      breakdownFields: [
        {
          key: 'category',
          label: 'P&L Category'
        },
        {
          key: 'description',
          label: 'Expense Description'
        },
        {
          key: 'monthly_expense',
        },
        {
          key: 'annual_expense',
        },
        {
          key: 'actions',
        }
      ],
      examples: [
        {category: 'Compensation', description: '<strong>Start Farming Campaign in April</strong><br><ul><li>Farming Campaign will be for the Willowbend neighborhood. Approx 2,000 homes. Cost will be $0.79 per mailer. Mailers will go out each month. Total monthly cost (total homes x cost per mailer) $1,580. Yearly Expense (April - December) $14,220.</li></ul>', monthly_expense: '1580', annual_expense: '14220'}
      ],
      perPage: 50,
      currentPage: 1,
      editable: null,
    };
  },
  computed: {
    ...mapGetters({
      user: 'observed/observed',
      subBasic: 'observed/subBasic',
      subStandard: 'observed/subStandard',
      subPremium: 'observed/subPremium',
    }),
    userRoute() {
      return process.env.VUE_APP_API_BASE_URL + '/users/' + this.$route.params.user;
    },
    subscribed() {
      return this.subBasic || this.subStandard || this.subPremium;
    },
    rows() {
      return this.items.length;
    },
    allocationTotal() {
      const companyGci = this.companyGci;

      return this.reccoItems.reduce(function (total, value) {
        return total + ((Number(value.annual_expense) / companyGci) * 100);
      }, 0);
    },
    amountTotal() {
      const companyGci = this.companyGci;

      return this.reccoItems.reduce(function (total, value) {
        return total + (companyGci * (value.allocation / 100));
      }, 0);
    },
    expenseTotal() {
      const companyGci = this.companyGci;

      return this.reccoItems.reduce(function (total, value) {
        return total + ((companyGci * (value.allocation / 100)) / 12);
      }, 0);
    },
    breakdownAllocation() {
      const companyGci = this.companyGci;

      return this.budgetModel.reduce(function (total, value) {
        return total + ((Number(value.annual_expense) / companyGci) * 100);
      }, 0);
    },
    annualTotal() {
      return this.budgetModel.reduce(function (total, value) {
        return total + value.annual_expense;
      }, 0);
    },
    monthlyTotal() {
      return this.budgetModel.reduce(function (total, value) {
        return total + value.monthly_expense;
      }, 0);
    }
  },
  methods: {
    async getData() {
      axios.get(this.userRoute + "/expense-breakdowns?year=" + this.year)
        .then(response => {
          this.items = response.data;
          this.loading = false;
        })
        .catch(error => {
          this.error(error);
        });
    },
    async getBudgetModel() {
      axios.get(this.userRoute + "/budget-model?year=" + this.year)
        .then(response => {
          this.budgetModel = response.data;
        })
        .catch(error => {
          this.error(error);
        });
    },
    async getEconomicModel() {
      axios.get(this.userRoute + "/economic-model?year=" + this.year)
      .then(response => {
        this.economicModel = response.data;
      })
      .catch(error => {
        this.error(error);
      });
    },
    async getCos() {
      axios.get(this.userRoute + '/cost-of-sale?year=' + this.year)
      .then(response => {
        this.cos = response.data;
      })
      .catch(error => {
        this.error(error);
      });
    },
    async addExpense() {
      this.form.year = this.year;
      axios.post(this.userRoute + "/expense-breakdowns", this.form)
        .then(response => {
          swal.fire({
            title: `Success`,
            text: response.data,
            showConfirmButton: false,
            timer: 1750,
            icon: 'success'
          });
          this.getData();
          this.getBudgetModel();
          this.$refs.formValidator.reset();
          this.resetForm();
        })
        .catch(error => {
          this.error(error);
        });
    },
    handleDelete(row) {
      swal.fire({
        title: 'Are you sure?',
        text: `You won't be able to revert this!`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonClass: 'btn btn-success btn-fill',
        cancelButtonClass: 'btn btn-danger btn-fill',
        confirmButtonText: 'Yes, delete it!',
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.deleteExpense(row);
          swal.fire({
            title: 'Deleted!',
            text: `Your expense has been deleted.`,
            type: 'success',
            showConfirmButton: false,
            timer: 1500,
            icon: 'success'
          });
        }
      });
    },
    async deleteExpense(expense) {
      axios.delete(this.userRoute + "/expense-breakdowns/" + expense.item.id)
      .then(response => {
        this.items.splice(expense.index, 1);
        this.getBudgetModel();
      })
      .catch(error => {
        this.error(error);
      });
    },
    handleEdit(data) {
      this.editable = data.index;
    },
    async handleUpdate(data) {
      axios.patch(this.userRoute + "/expense-breakdowns/" + data.item.id, {
        category: data.item.category,
        description: data.item.description,
        monthly_expense: data.item.monthly_expense,
        annual_expense: data.item.annual_expense,
      })
      .then(response => {
        swal.fire({
          title: `Success`,
          text: response.data,
          showConfirmButton: false,
          timer: 1750,
          icon: 'success'
        });
        this.editable = null;
      })
      .catch(error => {
        this.error(error);
      });
    },
    handleCancel() {
      this.editable = null;
    },
    resetForm() {
      this.form.category = null;
      this.form.description = '';
      this.form.monthly_expense = null;
      this.form.annual_expense = null;
    }
  },
  created() {
    this.getData();
    this.getBudgetModel();
    this.getEconomicModel();
    this.getCos();
  },
  mounted() {
    document.title = this.year + ' Budget Model';
  },
  watch: {
    year(to, from) {
      this.getData();
      this.getBudgetModel();
      this.getEconomicModel();
      this.getCos();
      document.title = to + ' Budget Model';
    }
  }
};
</script>
<style lang="scss">
.bd-html p {
    font-size: 0.8125rem;
    font-weight: normal;
}

td {
  ul {
    padding-left: 0.75rem;

    li {
      white-space: normal;
    }
  }
}
</style>